import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Building2, Clock } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	MoveProjectDialog,
	type MoveProjectInput,
} from "@/components/organizations/move-project-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	type SubscriptionScope,
	scopeLabelKey,
	statusLabelKey,
	statusToVariant,
} from "@/lib/subscription-display";
import { client } from "@/utils/orpc";

export const Route = createFileRoute("/organizations/$organizationId")({
	component: OrganizationDetailPage,
});

type DetailSubscription = {
	id: string;
	scope: SubscriptionScope;
	status: string;
	planItemPriceId: string;
	planName: string | null;
	planQuantity: number | null;
	projectId: string | null;
	projectName: string | null;
	currentTermStart: string | Date | null;
	currentTermEnd: string | Date | null;
	nextBillingAt: string | Date | null;
	pendingChange: {
		effectiveAt: string | Date;
		items: Array<{ itemPriceId: string; targetQuantity: number }>;
	} | null;
};

function OrganizationDetailPage() {
	const { organizationId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [showHistory, setShowHistory] = useState(false);
	const queryClient = useQueryClient();

	const searchOrganizations = async (
		search: string,
	): Promise<Array<{ id: string; name: string }>> => {
		const result = await client.admin.organizations.listOrganizations({
			search,
			limit: 10,
			offset: 0,
		});
		return result.organizations;
	};

	const moveProject = async (
		projectId: string,
		input: MoveProjectInput,
	): Promise<void> => {
		await client.admin.organizations.moveProject({ projectId, ...input });
		toast.success(t("admin:organizations.move.moved"));
		await queryClient.invalidateQueries({
			queryKey: ["admin", "organizations"],
		});
	};

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "organizations", "detail", organizationId],
		queryFn: async () => {
			const result = await client.admin.organizations.getOrganizationById({
				organizationId,
			});
			return result;
		},
		staleTime: 2 * 60 * 1000, // 2 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
	});

	if (isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-12 w-64" />
				<Skeleton className="h-96 w-full" />
			</div>
		);
	}

	if (!data) {
		return (
			<div className="py-12 text-center">
				<p className="text-muted-foreground">
					{t("admin:organizations.detail.not_found")}
				</p>
			</div>
		);
	}

	const {
		organization,
		members,
		projects,
		billing,
		syncIssues = [],
		pendingInvitesCount,
	} = data;

	const subscriptions = (billing?.subscriptions ?? []) as DetailSubscription[];
	const liveSubscriptions = subscriptions.filter(
		(sub) => sub.status !== "cancelled",
	);
	const historySubscriptions = subscriptions.filter(
		(sub) => sub.status === "cancelled",
	);

	const fmtDate = (value: string | Date | null): string =>
		value ? new Date(value).toLocaleDateString() : "—";

	const renderSubscriptionRow = (sub: DetailSubscription) => {
		const term =
			sub.currentTermStart || sub.currentTermEnd
				? `${fmtDate(sub.currentTermStart)} – ${fmtDate(sub.currentTermEnd)}`
				: "—";
		return (
			<TableRow key={sub.id}>
				<TableCell>
					<Badge variant="outline">{t(scopeLabelKey(sub.scope))}</Badge>
				</TableCell>
				<TableCell>
					<Badge variant={statusToVariant(sub.status)}>
						{t(statusLabelKey(sub.status))}
					</Badge>
				</TableCell>
				<TableCell>
					<span className="font-medium">
						{sub.planName ?? sub.planItemPriceId}
					</span>
					{sub.planQuantity && sub.planQuantity > 1 ? (
						<span className="text-muted-foreground text-xs">
							{" "}
							×{sub.planQuantity}
						</span>
					) : null}
				</TableCell>
				<TableCell className="text-muted-foreground text-sm">
					{sub.projectName ?? (sub.projectId ? sub.projectId : "—")}
				</TableCell>
				<TableCell className="text-muted-foreground text-sm">{term}</TableCell>
				<TableCell className="text-sm">
					{sub.pendingChange ? (
						<div className="flex items-center gap-1">
							<Clock className="h-3 w-3 text-muted-foreground" />
							<span>
								{sub.pendingChange.items.length > 0
									? sub.pendingChange.items
											.map((item) =>
												item.targetQuantity > 1
													? t(
															"admin:organizations.subscriptions.scheduled_change.to_quantity",
															{
																plan: item.itemPriceId,
																quantity: item.targetQuantity,
															},
														)
													: t(
															"admin:organizations.subscriptions.scheduled_change.to",
															{
																plan: item.itemPriceId,
															},
														),
											)
											.join(", ")
									: t(
											"admin:organizations.subscriptions.scheduled_change.badge",
										)}{" "}
								<span className="text-muted-foreground text-xs">
									{t(
										"admin:organizations.subscriptions.scheduled_change.effective",
										{
											date: fmtDate(sub.pendingChange.effectiveAt),
										},
									)}
								</span>
							</span>
						</div>
					) : (
						"—"
					)}
				</TableCell>
			</TableRow>
		);
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center gap-4">
				<Button
					variant="ghost"
					size="icon"
					onClick={() => navigate({ to: "/organizations" })}
				>
					<ArrowLeft className="h-4 w-4" />
				</Button>
				<div className="flex items-center gap-4">
					{organization.logo ? (
						<img
							src={organization.logo}
							alt={organization.name}
							className="h-12 w-12 rounded object-cover"
						/>
					) : (
						<div className="flex h-12 w-12 items-center justify-center rounded bg-muted">
							<Building2 className="h-6 w-6 text-muted-foreground" />
						</div>
					)}
					<div>
						<h1 className="font-bold text-3xl">{organization.name}</h1>
						<p className="text-muted-foreground text-sm">
							{t("admin:organizations.detail.created_at", {
								date: new Date(organization.createdAt).toLocaleDateString(),
							})}
						</p>
					</div>
				</div>
			</div>

			{/* Overview Cards */}
			<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
				<Card>
					<CardHeader className="pb-3">
						<CardTitle className="text-sm">
							{t("admin:organizations.detail.total_members")}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="font-bold text-2xl">{members.length}</div>
						{pendingInvitesCount > 0 && (
							<p className="text-muted-foreground text-xs">
								{t("admin:organizations.detail.pending_invites", {
									count: pendingInvitesCount,
								})}
							</p>
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-3">
						<CardTitle className="text-sm">
							{t("admin:organizations.detail.total_projects")}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="font-bold text-2xl">{projects.length}</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-3">
						<CardTitle className="text-sm">
							{t("admin:organizations.detail.subscription_status")}
						</CardTitle>
					</CardHeader>
					<CardContent>
						{liveSubscriptions.length > 0 ? (
							<div className="flex flex-wrap gap-1">
								{liveSubscriptions.map((sub) => (
									<Badge key={sub.id} variant={statusToVariant(sub.status)}>
										{t(scopeLabelKey(sub.scope))}:{" "}
										{t(statusLabelKey(sub.status))}
									</Badge>
								))}
							</div>
						) : (
							<Badge variant="outline">
								{t("admin:organizations.no_subscription")}
							</Badge>
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-3">
						<CardTitle className="text-sm">
							{t("admin:organizations.detail.organization_id")}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p className="font-mono text-xs">{organization.id}</p>
					</CardContent>
				</Card>
			</div>

			{/* Subscriptions */}
			<Card>
				<CardHeader>
					<CardTitle>{t("admin:organizations.subscriptions.title")}</CardTitle>
					<CardDescription>
						{t("admin:organizations.subscriptions.description")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					{subscriptions.length === 0 ? (
						<p className="text-muted-foreground text-sm">
							{t("admin:organizations.subscriptions.none")}
						</p>
					) : (
						<>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>
											{t("admin:organizations.subscriptions.columns.type")}
										</TableHead>
										<TableHead>
											{t("admin:organizations.subscriptions.columns.state")}
										</TableHead>
										<TableHead>
											{t("admin:organizations.subscriptions.columns.plan")}
										</TableHead>
										<TableHead>
											{t("admin:organizations.subscriptions.columns.project")}
										</TableHead>
										<TableHead>
											{t("admin:organizations.subscriptions.columns.term")}
										</TableHead>
										<TableHead>
											{t(
												"admin:organizations.subscriptions.columns.scheduled_change",
											)}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{liveSubscriptions.map(renderSubscriptionRow)}
									{showHistory &&
										historySubscriptions.map(renderSubscriptionRow)}
								</TableBody>
							</Table>
							{historySubscriptions.length > 0 && (
								<Button
									variant="ghost"
									size="sm"
									className="mt-2"
									onClick={() => setShowHistory((prev) => !prev)}
								>
									{showHistory
										? t("admin:organizations.subscriptions.hide_history")
										: t("admin:organizations.subscriptions.show_history")}
								</Button>
							)}
						</>
					)}
				</CardContent>
			</Card>

			{syncIssues.length > 0 ? (
				<Card>
					<CardHeader>
						<CardTitle>
							{t(
								"admin:organizations.detail.billing_sync_issues",
								"Billing sync issues",
							)}
						</CardTitle>
						<CardDescription>
							{t(
								"admin:organizations.detail.billing_sync_issues_description",
								"Unresolved billing catalog or subscription issues that may block self-service changes.",
							)}
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>
										{t("admin:organizations.detail.severity", "Severity")}
									</TableHead>
									<TableHead>
										{t("admin:organizations.detail.issue_type", "Type")}
									</TableHead>
									<TableHead>
										{t("admin:organizations.detail.message", "Message")}
									</TableHead>
									<TableHead>
										{t("admin:organizations.detail.last_seen", "Last seen")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{syncIssues.map(
									(issue: {
										id: string;
										severity: string;
										issueType: string;
										message: string;
										lastSeenAt: Date;
									}) => (
										<TableRow key={issue.id}>
											<TableCell>
												<Badge
													variant={
														issue.severity === "blocking"
															? "destructive"
															: "secondary"
													}
												>
													{issue.severity}
												</Badge>
											</TableCell>
											<TableCell className="font-mono text-xs">
												{issue.issueType}
											</TableCell>
											<TableCell>{issue.message}</TableCell>
											<TableCell className="text-muted-foreground text-sm">
												{new Date(issue.lastSeenAt).toLocaleString()}
											</TableCell>
										</TableRow>
									),
								)}
							</TableBody>
						</Table>
					</CardContent>
				</Card>
			) : null}

			{/* Billing Information */}
			{billing && (
				<Card>
					<CardHeader>
						<CardTitle>
							{t("admin:organizations.detail.billing_information")}
						</CardTitle>
						<CardDescription>
							{t("admin:organizations.detail.billing_description")}
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="grid gap-4 md:grid-cols-2">
							<div>
								<p className="text-muted-foreground text-sm">
									{t("admin:organizations.detail.billing_email")}
								</p>
								<p className="font-medium">{billing.email}</p>
							</div>
							{billing.company && (
								<div>
									<p className="text-muted-foreground text-sm">
										{t("admin:organizations.detail.company")}
									</p>
									<p className="font-medium">{billing.company}</p>
								</div>
							)}
							{billing.firstName && (
								<div>
									<p className="text-muted-foreground text-sm">
										{t("admin:organizations.detail.contact_name")}
									</p>
									<p className="font-medium">
										{billing.firstName}{" "}
										{billing.lastName ? billing.lastName : ""}
									</p>
								</div>
							)}
							{billing.vatNumber && (
								<div>
									<p className="text-muted-foreground text-sm">
										{t("admin:organizations.detail.vat_number")}
									</p>
									<p className="font-medium">{billing.vatNumber}</p>
								</div>
							)}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Members */}
			<Card>
				<CardHeader>
					<CardTitle>{t("admin:organizations.detail.members")}</CardTitle>
					<CardDescription>
						{t("admin:organizations.detail.members_description")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					{members.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>
										{t("admin:organizations.detail.member_name")}
									</TableHead>
									<TableHead>
										{t("admin:organizations.detail.member_email")}
									</TableHead>
									<TableHead>
										{t("admin:organizations.detail.member_joined")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{members.map(
									(member: {
										id: string;
										userId: string;
										userName: string;
										userEmail: string;
										createdAt: Date;
									}) => (
										<TableRow key={member.id}>
											<TableCell className="font-medium">
												<Link
													to="/users/$userId"
													params={{ userId: member.userId }}
													className="hover:underline"
												>
													{member.userName}
												</Link>
											</TableCell>
											<TableCell className="font-mono text-sm">
												{member.userEmail}
											</TableCell>
											<TableCell className="text-muted-foreground text-sm">
												{new Date(member.createdAt).toLocaleDateString()}
											</TableCell>
										</TableRow>
									),
								)}
							</TableBody>
						</Table>
					) : (
						<p className="text-muted-foreground text-sm">
							{t("admin:organizations.detail.no_members")}
						</p>
					)}
				</CardContent>
			</Card>

			{/* Projects */}
			<Card>
				<CardHeader>
					<CardTitle>{t("admin:organizations.detail.projects")}</CardTitle>
					<CardDescription>
						{t("admin:organizations.detail.projects_description")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					{projects.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>
										{t("admin:organizations.detail.project_name")}
									</TableHead>
									<TableHead>
										{t("admin:organizations.detail.project_description")}
									</TableHead>
									<TableHead>
										{t("admin:organizations.detail.project_created")}
									</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							<TableBody>
								{projects.map(
									(project: {
										id: string;
										name: string;
										description: string | null;
										createdAt: Date;
									}) => (
										<TableRow key={project.id}>
											<TableCell className="font-medium">
												{project.name}
											</TableCell>
											<TableCell className="text-muted-foreground text-sm">
												{project.description || "-"}
											</TableCell>
											<TableCell className="text-muted-foreground text-sm">
												{new Date(project.createdAt).toLocaleDateString()}
											</TableCell>
											<TableCell className="text-right">
												<MoveProjectDialog
													project={project}
													sourceOrganizationId={organizationId}
													searchOrganizations={searchOrganizations}
													loadPreview={(targetOrganizationId) =>
														client.admin.organizations.previewProjectMove({
															projectId: project.id,
															targetOrganizationId,
														})
													}
													onMove={(input) => moveProject(project.id, input)}
												/>
											</TableCell>
										</TableRow>
									),
								)}
							</TableBody>
						</Table>
					) : (
						<p className="text-muted-foreground text-sm">
							{t("admin:organizations.detail.no_projects")}
						</p>
					)}
				</CardContent>
			</Card>

			{/* Metadata */}
			{(organization.retentionStartedAt || organization.metadata) && (
				<Card>
					<CardHeader>
						<CardTitle>{t("admin:organizations.detail.metadata")}</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							{organization.retentionStartedAt && (
								<div>
									<p className="text-muted-foreground text-sm">
										{t("admin:organizations.detail.retention_started")}
									</p>
									<p className="font-medium">
										{new Date(
											organization.retentionStartedAt,
										).toLocaleDateString()}
									</p>
								</div>
							)}
							{organization.retentionEndsAt && (
								<div>
									<p className="text-muted-foreground text-sm">
										{t("admin:organizations.detail.retention_ends")}
									</p>
									<p className="font-medium">
										{new Date(
											organization.retentionEndsAt,
										).toLocaleDateString()}
									</p>
								</div>
							)}
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
